import { Link } from "react-router-dom";
import { HiArrowRight, HiCheckCircle } from "react-icons/hi";
import { motion } from "framer-motion";
import { bubbleVariants } from "../../util/Constants";
import Counter from "../components/Counter";

interface Props {
    className?: string;
}

export default function HeroSection({ className = "" }: Props) {
    return (
        <section className={`relative overflow-hidden bg-web-background ${className}`}>
            {/* Background glow */}
            <div className="absolute inset-0 bg-web-radial-glow" />

            {/* Green blur ball */}
            <div className="absolute left-1/2 top-0 h-[40vw] w-[40vw] min-h-[250px] min-w-[250px] max-h-[500px] max-w-[500px] -translate-x-1/2 rounded-full bg-web-primary/20 blur-[140px]" />

            {/* Vignette overlay */}
            <div className="absolute inset-0 bg-web-radial z-10 pointer-events-none" />

            {/* Floating chat bubbles */}
            <motion.div
                className="absolute left-[15%] top-[18%] hidden lg:block"
                animate={bubbleVariants.drift1}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0 }}
            >
                <img src="/images/chat.svg" />
            </motion.div>

            <motion.div
                className="absolute left-[6%] top-[42%] hidden lg:block"
                animate={bubbleVariants.drift2}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            >
                <div className="rounded-[20px_20px_20px_6px] border border-white/[0.08] bg-white/[0.08] px-4 py-3 text-xs font-normal text-white/70">
                    Can I send bulk messages?
                </div>
            </motion.div>

            <motion.div
                className="absolute left-[6%] top-[58%] hidden lg:block"
                animate={bubbleVariants.drift3}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 3 }}
            >
                <img src="/images/people.svg" alt="People" />
            </motion.div>

            <motion.div
                className="absolute right-[30%] top-[3%] hidden lg:block"
                animate={bubbleVariants.drift4}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            >
                <img src="/images/messenger.svg" />
            </motion.div>

            <motion.div
                className="absolute right-[8%] top-[35%] hidden lg:block"
                animate={bubbleVariants.drift5}
                transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut", delay: 2.2 }}
            >
                <div className="rounded-[20px_20px_20px_6px] border border-white/[0.08] bg-white/[0.08] px-4 py-3 text-xs font-normal text-white/70">
                    Got it! How do I upgrade <br /> my plan?
                </div>
            </motion.div>

            <motion.div
                className="absolute right-[10%] top-[58%] hidden lg:block"
                animate={bubbleVariants.drift6}
                transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 4 }}
            >
                <div className="flex flex-row items-center gap-2 rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-md border border-web-primary/30 bg-web-primary/15 px-4 py-3 text-xs font-normal leading-4 text-[#9FFFD0] shadow-web-glow-soft">
                    Your campaign is live
                    <HiCheckCircle className="text-web-primary text-sm" />
                </div>
            </motion.div>

            {/* Floating chat bubbles end */}


            {/* Main content */}
            <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center text-center">
                <div className="mb-8 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 lg:px-4 py-2 px-2 text-xs text-white/80 backdrop-blur-sm">
                    <img src="/images/greenstar.svg" alt="Whatsapp management" />
                    <span>Now with AI bot training — v2.4 shipping</span>
                </div>

                <h1 className="max-w-5xl text-5xl font-bold leading-tight text-white md:text-7xl">
                    WhatsApp Automation,
                    <br />
                    <span className="text-web-primary">Enterprise Grade.</span>
                </h1>

                <p className="mt-7 max-w-3xl text-lg leading-7 text-white md:text-2xl">
                    Send 10,000+ messages, automate responses, train bots,
                    and track performance — all from one powerful dashboard.
                </p>

                <div className="mt-12 flex flex-col gap-4 sm:flex-row">
                    <Link
                        to="/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 rounded-xl bg-web-primary px-5 py-4 font-medium text-black shadow-web-glow transition-colors hover:bg-web-secondary hover:text-white"
                    >
                        Get Started Free
                        <HiArrowRight size={18} />
                    </Link>
                    <Link
                        to="/docs"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center rounded-xl border border-white/10 bg-white/10 px-5 py-4 text-white backdrop-blur-md transition-colors hover:bg-white/15"
                    >
                        View API Docs
                    </Link>
                </div>

                {/* Stats */}
                <div className="mt-24 grid w-full max-w-6xl grid-cols-2 overflow-hidden rounded-3xl border border-white/10 backdrop-blur-sm md:grid-cols-4">
                    <div className="border-white/10 lg:px-16 px-2 py-4 border-r">
                        <h3 className="text-2xl font-bold text-web-primary">
                              <Counter end={10000} suffix="+" />
                        </h3>
                        <p className="mt-2 text-xs uppercase tracking-widest text-white">
                            Messages Sent
                        </p>
                    </div>

                    <div className="border-white/10 lg:px-16 px-2  py-4 border-r">
                        <h3 className="text-2xl font-bold text-web-primary">
                              <Counter end={42} suffix="+" />
                        </h3>
                        <p className="mt-2 text-xs uppercase tracking-widest text-white">
                            Active Clients
                        </p>
                    </div>

                    <div className="border-white/10 lg:px-16 px-2 py-4 border-r border-t lg:border-t-0">
                        <h3 className="text-2xl font-bold text-web-primary">98.02%</h3>
                        <p className="mt-2 text-xs uppercase tracking-widest text-white">
                            Delivery Rate
                        </p>
                    </div>

                    <div className="lg:px-16 px-2 py-4 border-t border-white/10 lg:border-t-0">
                        <h3 className="text-2xl font-bold text-web-primary">Real-time</h3>
                        <p className="mt-2 text-xs uppercase tracking-widest text-white">
                            Analytics
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}